import React from "react";
import { toUpper } from "lodash";
import { Card } from "@/shared/ui/Card";
import { Row } from "@/shared/ui/Blocks";
import { Icon, IconName } from "@/shared/ui/Icon";
import { cn } from "@/shared/utils/shadcn";
import { AvatarWithInitials } from "@/shared/ui/Avatar";
import ScoreChartGradient from "@/shared/widgets/Charts/ScoreChart/ScoreChart.gradient";
import { CompetencyInsight } from "@/shared/hooks/insights/useCompetencyInsights";
import { Person } from "@/features/people/graphql/generated";
import { ValuesInsight } from "@/shared/hooks/insights/useValuesInsights";
import { CandidateCompetencyInsight } from "@/shared/hooks/insights/useCandidateCompetencyInsights";

interface InsightCardProps {
  insight: CompetencyInsight | ValuesInsight | CandidateCompetencyInsight;
  person?: Person;
  width?: number;
}

export function InsightCard({ insight, person, width }: InsightCardProps) {
  const renderPersonScore = (score: any) => {
    if (person) {
      return (
        <AvatarWithInitials
          size="xs"
          avatar={person?.avatar || ""}
          initials={person?.initials || ""}
        />
      );
    }
    return <></>;
  };

  return (
    <Card className="p-4 break-inside-avoid bg-grey-100 hover:bg-white border-grey-100 hover:border-grey-300">
      {insight.tags && (
        <Row className="gap-2 items-center mb-2">
          <Icon
            size="sm"
            color="blue"
            icon={
              (insight.highlight?.tag?.icon || insight.tags[0].icon) as IconName
            }
            className={cn(insight.tags[0].color, "w-4 h-4")}
          />
          <p
            className={cn(
              "text-sm font-semibold tracking-[1px]",
              insight.tags[0].color
            )}
          >
            {insight.highlight?.tag?.label
              ? toUpper(insight.highlight?.tag?.label)
              : toUpper(insight.tags[0].label)}
          </p>
        </Row>
      )}
      <h5 className="text-base leading-6 mb-2 max-w-[95%]">{insight.title}</h5>
      <p className="text-sm leading-5 font-light text-gray-600">
        {insight.description}
      </p>

      <Card className="p-4 mt-4">
        <ScoreChartGradient
          scores={insight.dimension.peopleScores}
          score={insight.dimension.score}
          showArea={insight.dimension.peopleScores.length > 1}
          areaColor="blue"
          averageScore={insight.dimension.averageScore.value}
          showAverageScoreNumber={false}
          renderScore={renderPersonScore}
          width={width}
        />
      </Card>
    </Card>
  );
}
